import React from "react";
import { Layout } from "../../../components/layout/Layout";
import { useAuthStore } from "../../../store/authStore";
import { useHomePage } from "../hooks/useHomePage";
import { useDateFilteredLeads } from "../../../hooks/useDateFilteredData";

// Sub-components
import { HeroSection } from "../components/HeroSection";
import { StatsSection } from "../components/StatsSection";
import { AboutSection } from "../components/AboutSection";
import { ModulesSection } from "../components/ModulesSection";
import { ProjectsGrid } from "../components/ProjectsGrid";
import { WorkflowSection } from "../components/WorkflowSection";
import { SupportSection } from "../components/SupportSection";
import SupportTicketModal from "../components/SupportTicketModal";

// Static Module Constants
import {
  GALLERY,
  STATS,
  FEATURES,
  WORKFLOW,
  QUICK_ACCESS_ITEMS,
} from "../constants/homeConstants";

export const HomePage = () => {
  const { user } = useAuthStore();
  const {
    navigate,
    activeGallery,
    setActiveGallery,
    isModalOpen,
    setIsModalOpen,
    handleQuickAccess,
  } = useHomePage(GALLERY.length);

  // Fetch all leads for dynamic stats
  const { data: leadsData } = useDateFilteredLeads({ limit: 10000 });

  const rawLeads = Array.isArray(leadsData)
    ? leadsData
    : leadsData?.leads || leadsData?.projects || leadsData?.data || [];
  const leadsArray = Array.isArray(rawLeads) ? rawLeads : [];

  const projectStages = [
    "cust_acceptance",
    "design",
    "design_approval",
    "final_quotation",
    "quotation_approval",
    "documents",
    "project_started",
  ];

  const isProject = (l) =>
    l.isMovedToProject === true ||
    l.isMovedToProject === "true" ||
    l.isMovedToProject === "project" ||
    (l.customerAcceptance?.status === "Accepted" &&
      projectStages.includes(l.leadStatus || l.status));

  const completedProjects = leadsArray.filter(
    (l) =>
      isProject(l) &&
      (l.status === "completed" || l.leadStatus === "completed"),
  ).length;
  const pipelineProjects = leadsArray.filter(
    (l) =>
      isProject(l) &&
      !["completed", "drop", "cancelled"].includes(l.status || l.leadStatus),
  ).length;
  const coveredCities = new Set(
    leadsArray.map((l) => l.city?.trim()).filter((c) => c && c.length > 0),
  ).size;

  const dynamicStats = [
    { value: completedProjects.toString(), label: "Completed Projects" },
    { value: coveredCities.toString(), label: "Covered Cities" },
    { value: pipelineProjects.toString(), label: "Pipeline Projects" },
    { value: "400+", label: "5 Star Ratings" },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-[#fafaf9] font-['DM_Sans',sans-serif] text-slate-900 overflow-x-hidden">
        {/* 1. Hero Slideshow & Greeting Block */}
        <HeroSection
          user={user}
          gallery={GALLERY}
          activeGallery={activeGallery}
          setActiveGallery={setActiveGallery}
          onGoToDashboard={() => {
            if (user?.role === 'owner' || user?.role === 'admin') {
              navigate("/");
            }
          }}
          onViewProjects={() => {
            if (user?.role === 'owner' || user?.role === 'admin') {
              navigate("/projects");
            }
          }}
        />

        {/* 2. Operations Metrics Quick Counters */}
        {/* Developer : Nithin
            Date : 26-06-2026
            Changes : Commented out StatsSection as per 'just for display' view layout request. */}
        {/* <StatsSection stats={dynamicStats} /> */}

        {/* 3. About Admin Capabilities Block */}
        {/* <AboutSection /> */}

        {/* 4. Core Application Modules Grid */}
        <ModulesSection features={FEATURES} />

        {/* 5. Tile Grid Navigation */}
        <ProjectsGrid
          items={QUICK_ACCESS_ITEMS}
          onAccess={(item) => {
            /* Developer : Nithin
               Date : 26-06-2026
               Changes : Disabled all navigation from Home to keep it 'just for view'.
            if (user?.role === 'owner' || user?.role === 'admin' || item.label === 'Support') {
              handleQuickAccess(item);
            }
            */
          }}
        />

        {/* 6. Linear Operational Timelines */}
        <WorkflowSection workflowSteps={WORKFLOW} />

        {/* 7. Support Office Coordinates & Direct Modal trigger */}
        <SupportSection onOpenTicket={() => setIsModalOpen(true)} />

        {/* Support Request Ticket Overlay Modal */}
        <SupportTicketModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />

        {/* Footer Section */}
        <footer className="w-full pt-6 border-t border-slate-200">
          <p className="text-center text-sm tracking-wide text-slate-500">
            Designed by{" "}
            <span className="font-semibold text-slate-800">
              PINKMOON Technologies Pvt Ltd.
            </span>
          </p>
        </footer>
      </div>
    </Layout>
  );
};
